import type { TableRowType } from 'components/editor/grid/reduxStore/table.types';
import { RowTypesIdentifier } from 'components/editor/grid/reduxStore/table.types';
import { isGeneratedRow } from 'components/editor/grid/reduxStore/crmRowMarkers';
import type { CrmArrayLeaf } from './crmArrayPayload';
import type { CrmInstanceResolver } from './deriveCrmRows';
import { resolveInstancesForRow } from './deriveCrmRows';
import { chipNamesIn, instanceChipNode, parseTipTapDoc, transformChips } from './crmChipDocs';

export type PropagateParentCellArgs = {
  rows: TableRowType[];
  parentRowId: string;
  columnField: string;
  resolveInstances: CrmInstanceResolver;
};

/**
 * Mirrors a manual edit of the authored pattern row into its generated children:
 * static content is copied verbatim, CRM chips are re-resolved per child via the
 * child's `crmInstanceObjectId` and baked as self-resolved instance chips.
 */
export function propagateParentCellToChildren(args: PropagateParentCellArgs): TableRowType[] {
  const { rows, parentRowId, columnField, resolveInstances } = args;

  const parent = rows.find((row) => row.id === parentRowId);
  if (!parent) return rows;
  if (isGeneratedRow(parent)) return rows;
  if (parent.rowType !== RowTypesIdentifier.BODY) return rows;

  const hasGeneratedChildren = rows.some((row) => row.crmSourceRowId === parentRowId && isGeneratedRow(row));
  if (!hasGeneratedChildren) return rows;

  const parentValue = parent[columnField];

  const instancesByChip = new Map<string, CrmArrayLeaf[]>();
  chipNamesIn(parentValue).forEach((fieldRef) => {
    if (instancesByChip.has(fieldRef.name)) return;
    const instances = resolveInstancesForRow(parent, fieldRef, resolveInstances, columnField);
    if (instances) instancesByChip.set(fieldRef.name, instances);
  });

  const doc = parseTipTapDoc(parentValue);

  return rows.map((row) => {
    if (row.crmSourceRowId !== parentRowId || !isGeneratedRow(row)) return row;

    if (!doc) {
      return { ...row, [columnField]: parentValue };
    }

    const transformed = transformChips(doc, (chipName) => {
      const instances = instancesByChip.get(chipName);
      if (!instances) return undefined;
      const instance = instances.find((inst) => inst.objectId === row.crmInstanceObjectId);
      return instance ? instanceChipNode(instance) : null;
    });
    return { ...row, [columnField]: JSON.stringify(transformed ?? doc) };
  });
}
